import { getErrorMessage } from "../utils/errorMessage";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { projectsAPI, headcountAPI, employeesAPI, customersAPI, rateMasterAPI } from "../api/client";
import { toast } from "react-toastify";
import "./ProjectsTab.css";
import { mayPerformWithRole } from "../utils/userAccess";
import { useMasters } from "../context/MastersContext";

/*
 * ============================================================
 * PROJECT MASTER OPTIONS
 * ============================================================
 */

const PROJECT_TYPE_OPTIONS = [
  "Services",
  "Products & IP",
  "Internal",
];

const PROJECT_OWNERSHIP_OPTIONS = {
  Services: [
    "Staffing",
    "On-site Transfer",
    "Turnkey",
    "Consulting",
  ],

  "Products & IP": [
    "Product Development",
    "Maintenance",
    "Implementation",
    "Assembly",
    "Manufacturing",
    "Licensing",
  ],

  Internal: [
    "Bench",
    "Capex",
  ],
};

const YES_NO_OPTIONS = ["Yes", "No"];

const CUSTOMER_PO_OPTIONS = [
  "Yes",
  "Pending",
];

const COMMERCIAL_MODEL_OPTIONS = [
  "Time & Material (T&M)",
  "Fixed fee for team",
  "Fixed price with milestone billing",
  "License fee",
  "Fixed fee per month",
  "Units based",
  "Others (Specify)",
];

const BILLING_FREQUENCY_OPTIONS = [
  "Monthly",
  "Milestone dates",
  "One time",
  "Units delivered",
];

const PROJECT_EXECUTION_OPTIONS = [
  "Customer Site",
  "UTS Office",
];

const PROJECT_LOCATION_OPTIONS = [
  "Bangalore",
  "Chennai",
  "Hyderabad",
  "Pune",
  "Mumbai",
  "Gurugram",
  "UK",
  "USA",
  "Dubai",
  "Canada",
  "Malaysia",
  "Germany",
];

const INDUSTRY_VERTICAL_OPTIONS = [
  "Automotive & Transportation",
  "Hi Tech- Data Centers",
  "Hi Tech- Semiconductors",
  "Hi Tech- Others",
  "Defence",
  "Aerospace",
  "Space",
  "Consumer",
  "Industrial Products",
  "Power",
  "Oil & Gas",
  "Med Devices",
];

const PO_SOW_MODEL_OPTIONS = [
  "Resource level",
  "Project level-role based rate card",
  "Customer level- role based rate card",
];

/*
 * Collapse a designation that is an exact doubled phrase
 * (e.g. "Application Engineer Application Engineer" -> "Application Engineer")
 * so lookups always use the actual designation.
 */
function cleanDesignation(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  const halfLen = Math.floor(trimmed.length / 2);
  if (trimmed.length % 2 === 0 && trimmed.slice(0, halfLen) === trimmed.slice(halfLen)) {
    return trimmed.slice(0, halfLen);
  }
  return trimmed;
}

const PROJECT_STATUS_OPTIONS = [
  "Draft",
  "Yet to start",
  "In progress",
  "Completed",
  "Abandoned",
];

const BILLING_RATE_TYPE_OPTIONS = [
  "Hourly rate",
  "Daily rate",
  "Monthly rate",
  "Not Applicable",
];

const OPTION_COLLATOR = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

function sortedOptions(options, getLabel = (option) => option) {
  return [...(options || [])].sort((a, b) =>
    OPTION_COLLATOR.compare(String(getLabel(a) ?? ""), String(getLabel(b) ?? ""))
  );
}

function SearchableSelect({ value, options, onChange, placeholder, disabled, getOptionValue, getOptionLabel, selectedDisplayValue, className = "" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const selected = options.find((option) => String(getOptionValue(option)) === String(value));
  const selectedLabel = selected ? getOptionLabel(selected) : (selectedDisplayValue || String(value || ""));
  const matchingOptions = options.filter((option) => getOptionLabel(option).toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const optionListHeight = matchingOptions.length ? Math.min(matchingOptions.length, 7) * 36 : 40;

  const updateMenuPosition = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const menuHeight = Math.min(optionListHeight + 60, window.innerHeight - 24);
    const below = window.innerHeight - rect.bottom - 8;
    const above = rect.top - 8;
    const opensAbove = below < menuHeight && above > below;
    const availableHeight = Math.max(150, Math.min(menuHeight, opensAbove ? above - 4 : below));
    setMenuPosition({
      position: "fixed",
      top: opensAbove ? Math.max(8, rect.top - availableHeight - 4) : Math.min(rect.bottom + 4, window.innerHeight - availableHeight - 8),
      left: Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8)),
      right: "auto",
      width: rect.width,
      maxHeight: availableHeight,
    });
  }, [optionListHeight]);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    updateMenuPosition();
    document.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    searchRef.current?.focus();
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  const closeWithValue = (option) => {
    onChange(getOptionValue(option));
    setOpen(false);
    setQuery("");
  };

  return (
    <div className={`pj-searchable-select ${className}`} ref={rootRef}>
      <button
        type="button"
        className={`pj-searchable-select-trigger ${!selectedLabel ? "is-placeholder" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{selectedLabel || placeholder}</span><span className="pj-searchable-select-arrow" aria-hidden="true">▾</span>
      </button>
      {open && !disabled && menuPosition && createPortal(
        <div className="pj-searchable-select-menu" ref={menuRef} style={menuPosition}>
          <input ref={searchRef} type="search" value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") event.preventDefault(); }} placeholder="Search..." aria-label={`Search ${placeholder}`} />
          <div className="pj-searchable-select-options" role="listbox" style={{ height: Math.min(optionListHeight, Math.max(40, menuPosition.maxHeight - 60)) }}>
            {matchingOptions.length ? matchingOptions.map((option) => (
              <button type="button" role="option" aria-selected={String(getOptionValue(option)) === String(value)} key={String(getOptionValue(option))} title={getOptionLabel(option)} onClick={() => closeWithValue(option)}>
                {getOptionLabel(option)}
              </button>
            )) : <div className="pj-searchable-select-empty">No matching options</div>}
          </div>
        </div>, document.body
      )}
    </div>
  );
}

const CURRENCY_OPTIONS = [
  "INR",
  "USD",
  "CAD",
  "Euro",
  "GBP",
  "AED",
];

const CURRENCY_LABELS = {
  INR: "INR",
  USD: "USD",
  CAD: "CAD",
  EUR: "EUR",
  Euro: "EUR",
  GBP: "GBP",
  AED: "AED",
};

function inrPerLabel(currency) {
  return `INR per ${CURRENCY_LABELS[currency] || currency || "INR"}`;
}

function inrValueOf(project) {
  const value = Number(project.poSowValue) || 0;
  if (project.currency === "INR") return value;
  const rate = Number(project.inrConversionRate) || 0;
  if (value <= 0 || rate <= 0) return null;
  return value * rate;
}

/*
 * ============================================================
 * MILESTONES (shown when Billing Frequency = "Milestone dates")
 * ============================================================
 */

const EMPTY_MILESTONES = [
  { description: "", date: "", amount: "" },
  { description: "", date: "", amount: "" },
  { description: "", date: "", amount: "" },
  { description: "", date: "", amount: "" },
];

function parseMilestones(raw) {
  if (Array.isArray(raw)) {
    return raw.length ? raw : null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch {
    return null;
  }
}

/*
 * ============================================================
 * INITIAL PROJECT FORM
 * ============================================================
 */

const INITIAL_PROJECT = {
  projectId: "",

  description: "",

  customerType: "External",
  internalCustomerId: null,

  endCustomer: "",
  endCustomerId: null,
  billingCustomer: "",
  billingCustomerId: null,

  projectType: "",
  projectOwnershipModel: "",

  projectBillability: "",
  commercialModel: "",
  billingFrequency: "",
  milestones: EMPTY_MILESTONES.map((m) => ({ ...m })),

  projectExecutionAt: "",
  projectLocation: "",

  industryVertical: "",
  businessUnit: "",
  division: "",
  department: "",

  utsBillingEntity: "",

  poSowWoModel: "",
  customerPoSowAvailable: "",
  projectPoSowRef: "",
  poSowStartDate: "",
  poSowEndDate: "",
  poSowValue: "",
  currency: "INR",
  inrConversionRate: 1,
  attachment: null,
  attachmentUrl: null,
  attachmentName: "",

  projectOwners: {
    delivery: "",
    deliveryId: "",
    sales: "",
    salesId: "",
    operations: "",
    operationsId: "",
  },

  doubleCreditApplicable: "",
  doubleCreditTo: "",

  projectStatus: "Yet to start",

  abandonedReason: "",
};

/*
 * ============================================================
 * ADD RESOURCE INITIAL STATE
 * ============================================================
 */

const INITIAL_RESOURCE = {
  name: "",
  employeeId: "",
  billable: "Yes",

  projectExecutionAt: "",
  projectLocation: "",

  billingStartDate: "",
  billingEndDate: "",

  poSowWoRef: "",
  poStartDate: "",
  poEndDate: "",

  poRole: "",

  billingRateType: "",
  billingRate: "",

  allocation: 100,

  estimatedRevenuePerMonth: "",
  estimatedCostPerMonth: "",
};

const isCustomerActive = (customer) => customer?.isActive === 1 || customer?.isActive === "1" || customer?.isActive === true;
const isCustomerFlagEnabled = (value) => [1, "1", true, "YES", "Y", "TRUE"].includes(typeof value === "string" ? value.trim().toUpperCase() : value);

/*
 * ============================================================
 * HELPER: Map DB row -> frontend project object
 * ============================================================
 */

function mapDbToProject(row) {
  return {
    id: row.id,
    projectId: row.project_id || "",

    description: row.description || "",

    customerType: row.customer_type || "External",
    internalCustomerId: row.internal_customer_id || null,

    endCustomer: row.end_customer || "",
    endCustomerId: row.end_customer_id || null,
    billingCustomer: row.billing_customer || "",
    billingCustomerId: row.billing_customer_id || null,

    projectType: row.project_type || "",
    projectOwnershipModel: row.project_ownership_model || "",

    projectBillability: row.project_billability || "",
    commercialModel: row.commercial_model || "",
    billingFrequency: row.billing_frequency || "",
    milestones: parseMilestones(row.milestones) || EMPTY_MILESTONES.map((m) => ({ ...m })),

    projectExecutionAt: row.project_execution_at || "",
    projectLocation: row.project_location || "",

    industryVertical: row.industry_vertical || "",
    businessUnit: row.business_unit || "",
    division: row.division || "",
    department: row.department || "",

    utsBillingEntity: row.uts_billing_entity || "",

    poSowWoModel: row.po_sow_wo_model || "",
    customerPoSowAvailable: row.customer_po_sow_available || "",
    projectPoSowRef: row.project_po_sow_ref || "",
    poSowStartDate: row.po_sow_start_date || "",
    poSowEndDate: row.po_sow_end_date || "",
    poSowValue: row.po_sow_value || "",
    currency: row.currency || "INR",
    inrConversionRate: row.currency === "INR"
      ? 1
      : (row.inr_conversion_rate == null ? "" : Number(row.inr_conversion_rate)),
    attachmentUrl: row.attachment_url || null,
    attachmentName: row.attachment_name || "",

    projectOwners: {
      delivery: row.project_owners_delivery || "",
      deliveryId: row.project_owners_delivery_id || "",
      sales: row.project_owners_sales || "",
      salesId: row.project_owners_sales_id || "",
      operations: row.project_owners_operations || "",
      operationsId: row.project_owners_operations_id || "",
    },

    doubleCreditApplicable: row.double_credit_applicable || "",
    doubleCreditTo: row.double_credit_to || "",

    projectStatus: row.project_status || "Draft",

    abandonedReason: row.abandoned_reason || "",

    createdBy: row.created_by_name || "",
    createdAt: row.created_at || "",
  };
}

function mapProjectToApi(project) {
  const currency = String(project.currency || "INR").toUpperCase();

  return {
    projectId: project.projectId || undefined,
    description: project.description,
    customerType: project.customerType || "External",
    internalCustomerId: project.customerType === "Internal" ? project.internalCustomerId : null,
    endCustomer: project.endCustomer,
    endCustomerId: project.endCustomerId,
    billingCustomer: project.billingCustomer,
    billingCustomerId: project.billingCustomerId,
    projectType: project.projectType,
    projectOwnershipModel: project.projectOwnershipModel,
    projectBillability: project.projectBillability,
    commercialModel: project.commercialModel,
    billingFrequency: project.billingFrequency,
    milestones: Array.isArray(project.milestones) ? project.milestones : undefined,
    projectExecutionAt: project.projectExecutionAt,
    projectLocation: project.projectLocation,
    industryVertical: project.industryVertical,
    businessUnit: project.businessUnit,
    division: project.division,
    department: project.department,
    utsBillingEntity: project.utsBillingEntity,
    poSowWoModel: project.poSowWoModel,
    customerPoSowAvailable: project.customerPoSowAvailable,
    projectPoSowRef: project.projectPoSowRef,
    poSowStartDate: project.poSowStartDate || null,
    poSowEndDate: project.poSowEndDate || null,
    poSowValue: project.poSowValue || 0,

    currency,

    inrConversionRate:
      currency === "INR"
        ? 1
        : Number(project.inrConversionRate),

    projectOwners: project.projectOwners,
    doubleCreditApplicable: project.doubleCreditApplicable,
    doubleCreditTo: project.doubleCreditTo,
    projectStatus: project.projectStatus,
    abandonedReason: project.abandonedReason || null,
  };
}

/*
 * ============================================================
 * HELPER: Status badge class
 * ============================================================
 */

function getStatusClass(status) {
  switch (status) {
    case "Draft": return "status-draft";
    case "Yet to start": return "status-yet-to-start";
    case "In progress": return "status-in-progress";
    case "Completed": return "status-completed";
    case "Abandoned": return "status-abandoned";
    default: return "";
  }
}

function ResourceDetailRow({ label, value }) {
  const display = value === null || value === undefined || value === "" ? "—" : String(value);
  return (
    <div className="pj-detail-item">
      <span className="pj-detail-label">{label}</span>
      <span className="pj-detail-value">{display}</span>
    </div>
  );
}

/*
 * ============================================================
 * MAIN COMPONENT
 * ============================================================
 */

const ProjectsTab = () => {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const canCreateProject = mayPerformWithRole(user?.role, "project", "add", "project.create");
  const canEditProject = mayPerformWithRole(user?.role, "project", "edit", "project.edit");
  const canDeleteProject = mayPerformWithRole(user?.role, "project", "delete", "project.delete");

  /* --- Master (DB-backed) option lists --- */
  const { getList } = useMasters();
  const PROJECT_TYPE_OPTIONS = getList("project_type");
  const COMMERCIAL_MODEL_OPTIONS = getList("commercial_model");
  const BILLING_FREQUENCY_OPTIONS = getList("billing_frequency");
  const PROJECT_EXECUTION_OPTIONS = getList("project_execution");
  const PROJECT_LOCATION_OPTIONS = getList("project_location");
  const INDUSTRY_VERTICAL_OPTIONS = getList("industry_vertical");
  const PO_SOW_MODEL_OPTIONS = getList("po_sow_model");
  const PROJECT_STATUS_OPTIONS = getList("project_status");
  const BILLING_RATE_TYPE_OPTIONS = getList("billing_rate_type");
  const CURRENCY_OPTIONS = getList("currency");
  const PROJECT_OWNERSHIP_OPTIONS = {
    Services: getList("project_ownership_Services"),
    "Products & IP": getList("project_ownership_Products & IP"),
    Internal: getList("project_ownership_Internal"),
  };

  /* --- View State --- */
  const [view, setView] = useState("list");

  /* --- List State --- */
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [divisionFilter, setDivisionFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [billabilityFilter, setBillabilityFilter] = useState("");
  const [ownershipFilter, setOwnershipFilter] = useState("");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showDivisionDropdown, setShowDivisionDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showBillabilityDropdown, setShowBillabilityDropdown] = useState(false);
  const [showOwnershipDropdown, setShowOwnershipDropdown] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  /* --- Form State --- */
  const [project, setProject] = useState(INITIAL_PROJECT);
  const [resources, setResources] = useState([]);
  const [resourceForm, setResourceForm] = useState(INITIAL_RESOURCE);
  const [resourceCustomer, setResourceCustomer] = useState({ customerId: null, customerName: "" });
  const [showResourceForm, setShowResourceForm] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [closureChecklist, setClosureChecklist] = useState([]);

  /* --- Resource View Modal --- */
  const [resourceToView, setResourceToView] = useState(null);

  /* --- Delete Modal --- */
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  /* --- Attachment Upload --- */
  const [attachmentUploading, setAttachmentUploading] = useState(false);

  /* --- Dropdown Options (from master data) --- */
  const [dropdownOptions, setDropdownOptions] = useState({
    businessUnits: [],
    divisions: [],
    departments: [],
    companies: [],
    designations: [],
  });

  const [filteredDivisionsByBU, setFilteredDivisionsByBU] = useState([]);
  const [filteredDepartmentsByDivBU, setFilteredDepartmentsByDivBU] = useState([]);
  const buRequestRef = useRef(0);
  const divRequestRef = useRef(0);

  /* --- Employee Search for Resource Form --- */
  const [empSearchResults, setEmpSearchResults] = useState([]);
  const [showEmpDropdown, setShowEmpDropdown] = useState(false);
  const [empSearchTerm, setEmpSearchTerm] = useState("");
  const empDropdownRef = useRef(null);

  /* --- Customer Master options for Billing/End Customer dropdowns --- */
  const [customerMaster, setCustomerMaster] = useState([]);

  /* --- Owner Search for Delivery/Sales/Operations --- */
  const [ownerSearchResults, setOwnerSearchResults] = useState([]);
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [ownerSearchField, setOwnerSearchField] = useState("");
  const [ownerSearchTerm, setOwnerSearchTerm] = useState("");
  const ownerDropdownRef = useRef(null);

  /* --- Departments filtered by selected Division --- */
  const [filteredDepartments, setFilteredDepartments] = useState([]);

  /* --- Resource Cost Preview --- */
  const [resourceCostPreview, setResourceCostPreview] = useState({ estimatedCostPerMonth: 0, estimatedRevenuePerMonth: 0, found: false });

  /* --- Employee Project Allocation Summary (from project_resources) ---
     Allocation is managed PER PROJECT (0%-100%). There is no employee-wide
     100% limitation, so combined totals across projects may exceed 100%. */
  const [employeeAllocation, setEmployeeAllocation] = useState({ totalAllocated: 0, projectCount: 0, loading: false });

  /* --- Rate Master Auto-Fetch (for role-based billing) --- */
  const [rateLookup, setRateLookup] = useState({ found: false, rate: null, loading: false, searched: false });

  /* --- Role options from Rate Master (PO Role dropdown under role-based cards) --- */
  const [rateMasterRoles, setRateMasterRoles] = useState([]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (empDropdownRef.current && !empDropdownRef.current.contains(e.target)) {
        setShowEmpDropdown(false);
      }
      if (ownerDropdownRef.current && !ownerDropdownRef.current.contains(e.target)) {
        setShowOwnerDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showEmpDropdown || !empSearchTerm || empSearchTerm.trim().length < 1) {
      setEmpSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await employeesAPI.getAll({ search: empSearchTerm, status: "Active", limit: 10 });
        const list = Array.isArray(res.data?.employees) ? res.data.employees : (Array.isArray(res.data) ? res.data : []);
        setEmpSearchResults(list.slice(0, 10));
      } catch {
        setEmpSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [empSearchTerm, showEmpDropdown]);

  // Fetch current customer status whenever the project form opens. Customer
  // Master checkbox flags and active status control new customer selections.
  useEffect(() => {
    if (view !== "create") return;
    let active = true;
    customersAPI.getAll()
      .then((res) => { if (active) setCustomerMaster(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (active) setCustomerMaster([]); });
    return () => { active = false; };
  }, [view]);

  // Owner search for Delivery/Sales/Operations fields
  useEffect(() => {
    if (!showOwnerDropdown || !ownerSearchTerm || ownerSearchTerm.trim().length < 1) {
      setOwnerSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await employeesAPI.getAll({ search: ownerSearchTerm, status: "Active", limit: 10 });
        const list = Array.isArray(res.data?.employees) ? res.data.employees : (Array.isArray(res.data) ? res.data : []);
        setOwnerSearchResults(list.slice(0, 10));
      } catch {
        setOwnerSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [ownerSearchTerm, showOwnerDropdown]);

  // Live cost/revenue/profit preview from the backend when employee,
  // execution location, allocation or billing rate fields change.
  useEffect(() => {
    if (!resourceForm.employeeId && !resourceForm.billingRate) {
      setResourceCostPreview({ estimatedCostPerMonth: 0, estimatedRevenuePerMonth: 0, found: false });
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await projectsAPI.getResourceCostPreview({
          projectId: project.id || project.projectId,
          employeeId: resourceForm.employeeId,
          projectExecutionAt: resourceForm.projectExecutionAt,
          allocation: resourceForm.allocation,
          billingRate: resourceForm.billingRate,
          billingRateType: resourceForm.billingRateType,
          poRole: resourceForm.poRole,
        });
        setResourceCostPreview(res.data);
      } catch {
        setResourceCostPreview({ estimatedCostPerMonth: 0, estimatedRevenuePerMonth: 0, found: false });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [resourceForm.employeeId, resourceForm.projectExecutionAt, resourceForm.allocation, resourceForm.billingRate, resourceForm.billingRateType, resourceForm.poRole, project.id, project.projectId]);

  // Auto-fetch billing rate from Rate Master when role-based PO model + PO Role + Billing Rate Type are set
  const isRoleBasedPO = project.poSowWoModel === "Project level-role based rate card" || project.poSowWoModel === "Customer level- role based rate card";
  const isProjectLevelPO = project.poSowWoModel === "Project level-role based rate card";
  const isCustomerLevelPO = project.poSowWoModel === "Customer level- role based rate card";

  useEffect(() => {
    if (!isRoleBasedPO || !resourceForm.poRole || !resourceForm.billingRateType) {
      setRateLookup({ found: false, rate: null, loading: false, searched: false });
      return;
    }
    const timer = setTimeout(async () => {
      setRateLookup((prev) => ({ ...prev, loading: true }));
      try {
        const params = {
          designation: resourceForm.poRole,
          commercialModel: project.commercialModel || undefined,
          billingRateType: resourceForm.billingRateType,
          currency: project.currency || "INR",
        };
        // Scope the Rate Master lookup to the project's commercial model:
        //   Project model  -> Project-scoped rate (falls back to Common)
        //   Customer model -> Customer-scoped rate (falls back to Common)
        if (isProjectLevelPO && project.projectId) {
          params.projectId = project.projectId;
        } else if (isCustomerLevelPO && resourceCustomer.customerId) {
          params.customerId = resourceCustomer.customerId;
        }
        const res = await rateMasterAPI.getMatch(params);
        if (res.data.found) {
          const r = res.data.rate;
          setRateLookup({ found: true, rate: r, loading: false, searched: true });
          setResourceForm((prev) => ({ ...prev, billingRate: String(r.rate) }));
        } else {
          setRateLookup({ found: false, rate: null, loading: false, searched: true });
          setResourceForm((prev) => ({ ...prev, billingRate: "" }));
        }
      } catch {
        setRateLookup({ found: false, rate: null, loading: false, searched: true });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [isRoleBasedPO, isProjectLevelPO, isCustomerLevelPO, resourceForm.poRole, resourceForm.billingRateType, project.commercialModel, project.currency, resourceCustomer.customerId, project.projectId]);

  /*
   * ----------------------------------------------------------
   * FETCH PROJECTS LIST
   * ----------------------------------------------------------
   */

  const fetchProjects = useCallback(async () => {
    setListLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.projectType = typeFilter;
      if (divisionFilter) params.division = divisionFilter;
      if (locationFilter) params.projectLocation = locationFilter;
      if (billabilityFilter) params.billability = billabilityFilter;
      if (ownershipFilter) params.ownership = ownershipFilter;

      const res = await projectsAPI.getAll(params);
      const nextTotal = Number(res.data.total) || 0;
      const lastPage = Math.max(1, Math.ceil(nextTotal / limit));
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
      setProjects(res.data.projects || []);
      setTotal(nextTotal);
    } catch (err) {
      console.error("Failed to fetch projects:", err);
      toast.error("Failed to load projects");
    } finally {
      setListLoading(false);
    }
  }, [page, limit, search, statusFilter, typeFilter, divisionFilter, locationFilter, billabilityFilter, ownershipFilter]);

  useEffect(() => {
    if (view === "list") {
      fetchProjects();
    }
  }, [view, fetchProjects]);

  const clearAllFilters = () => {
    setSearch("");
    setStatusFilter("");
    setTypeFilter("");
    setDivisionFilter("");
    setLocationFilter("");
    setBillabilityFilter("");
    setOwnershipFilter("");
    setPage(1);
  };

  const filterToolbarRef = useRef(null);

  const closeAllDropdowns = () => {
    setShowStatusDropdown(false);
    setShowTypeDropdown(false);
    setShowDivisionDropdown(false);
    setShowLocationDropdown(false);
    setShowBillabilityDropdown(false);
    setShowOwnershipDropdown(false);
  };

  // Clicking anywhere outside the filter toolbar closes every open filter
  // dropdown (native <select> filters already close on outside click).
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterToolbarRef.current && !filterToolbarRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
        setShowTypeDropdown(false);
        setShowDivisionDropdown(false);
        setShowLocationDropdown(false);
        setShowBillabilityDropdown(false);
        setShowOwnershipDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const ownershipOptionsFlat = useMemo(() => {
    const unique = new Set();
    Object.values(PROJECT_OWNERSHIP_OPTIONS).forEach((list) => list.forEach((o) => unique.add(o)));
    return sortedOptions([...unique]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasActiveFilters = Boolean(search || statusFilter || typeFilter || divisionFilter || locationFilter || billabilityFilter || ownershipFilter);

  const renderFilterDropdown = ({ label, allLabel, values, value, onSelect, open, onToggle }) => (
    <div className="filter-dropdown">
      <button
        type="button"
        className={`filter-trigger ${value ? "active" : ""}`}
        onClick={onToggle}
      >
        <span>{label}</span>
        <span className="filter-value">{value || allLabel}</span>
        <span className="filter-arrow">↓</span>
      </button>
      {open && (
        <div className="filter-menu">
          <button
            className={`filter-option ${value === "" ? "selected" : ""}`}
            onClick={() => { onSelect(""); closeAllDropdowns(); }}
          >
            {allLabel}
          </button>
          {sortedOptions(values).map((v) => (
            <button
              key={v}
              className={`filter-option ${value === v ? "selected" : ""}`}
              onClick={() => { onSelect(v); closeAllDropdowns(); }}
            >
              {v}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  useEffect(() => {
    headcountAPI.getOptions().then((res) => {
      const rawDesignations = res.data.designations || [];
      setDropdownOptions({
        businessUnits: res.data.businessUnits || [],
        divisions: res.data.divisions || [],
        departments: res.data.departments || [],
        companies: res.data.companies || [],
        designations: [...new Set(rawDesignations.map(cleanDesignation))].filter(Boolean),
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isRoleBasedPO || !project.commercialModel) {
      setRateMasterRoles([]);
      return;
    }
    let active = true;
    const params = { commercialModel: project.commercialModel };
    // Scope the role list by the project's commercial model so the
    // applicable project / customer / common rates appear as roles.
    if (isProjectLevelPO && project.projectId) {
      params.projectId = project.projectId;
    } else if (isCustomerLevelPO) {
      const custId = resourceCustomer.customerId || project.endCustomerId || null;
      if (custId) {
        params.customerId = custId;
      }
    }
    rateMasterAPI.getRoles(params)
      .then((res) => {
        if (active) setRateMasterRoles(Array.isArray(res.data?.roles) ? res.data.roles : []);
      })
      .catch(() => {
        if (active) setRateMasterRoles([]);
      });
    return () => { active = false; };
  }, [isRoleBasedPO, isProjectLevelPO, isCustomerLevelPO, project.commercialModel, project.currency, resourceCustomer.customerId, project.endCustomerId, project.projectId]);

  const buOptions = useMemo(() => {
    return (dropdownOptions.businessUnits || []).filter(Boolean);
  }, [dropdownOptions.businessUnits]);

  const getBillingCompanyForBU = useCallback((bu) => {
    if (!bu) return "";
    const buUpper = String(bu).toUpperCase();
    if (buUpper.includes("ELDAAS")) return "ELDAAS Technologies Pvt Ltd";
    if (buUpper.includes("EXCEL")) return "Excel VLSI Technologies Pvt Ltd";
    if (buUpper.includes("SELENEAN")) return "Selenean Technologies Private Limited";
    if (buUpper.includes("UTS")) return "Univision Technology Solutions Private Limited";
    if (buUpper.includes("USS")) return "Univision Systems & Solutions Private Limited";
    return "";
  }, []);

  useEffect(() => {
    if (project.businessUnit) {
      const requestId = ++buRequestRef.current;
      headcountAPI.getDivisionsByBU(project.businessUnit)
        .then((res) => {
          if (requestId !== buRequestRef.current) return;
          setFilteredDivisionsByBU(res.data.divisions || []);
        })
        .catch(() => {
          if (requestId !== buRequestRef.current) return;
          setFilteredDivisionsByBU([]);
        });
    } else {
      setFilteredDivisionsByBU([]);
    }
  }, [project.businessUnit]);

  useEffect(() => {
    if (!project.division) {
      setFilteredDepartments([]);
      setFilteredDepartmentsByDivBU([]);
      return;
    }
    const requestId = ++divRequestRef.current;
    headcountAPI.getDepartmentsByDivision(project.division, project.businessUnit)
      .then((res) => {
        if (requestId !== divRequestRef.current) return;
        const depts = res.data.departments || [];
        setFilteredDepartments(depts);
        setFilteredDepartmentsByDivBU(depts);
      })
      .catch(() => {
        if (requestId !== divRequestRef.current) return;
        setFilteredDepartments([]);
        setFilteredDepartmentsByDivBU([]);
      });
  }, [project.division, project.businessUnit]);

  /*
   * ----------------------------------------------------------
   * PROJECT TYPE
   * ----------------------------------------------------------
   */

  const ownershipOptions = useMemo(() => {
    return PROJECT_OWNERSHIP_OPTIONS[project.projectType] || [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.projectType]);

  /*
   * ----------------------------------------------------------
   * CUSTOMER MASTER DROPDOWN OPTIONS
   * Customer Master checkboxes are the source of truth:
   *   Billing Customer dropdown -> billing_customer = YES
   *   End Customer dropdown     -> end_customer = YES
   * Previously-saved customer values are rendered in the closed field by the
   * select's selectedDisplayValue prop when they are no longer eligible.
   * ----------------------------------------------------------
   */

  const billingCustomerOptions = useMemo(() => {
    return customerMaster.filter((c) => isCustomerActive(c) && isCustomerFlagEnabled(c.billingCustomer));
  }, [customerMaster]);

  const endCustomerOptions = useMemo(() => {
    return customerMaster.filter((c) => isCustomerActive(c) && isCustomerFlagEnabled(c.endCustomer));
  }, [customerMaster]);

  const internalCustomerOptions = useMemo(() => (
    customerMaster.filter((c) => isCustomerActive(c) && isCustomerFlagEnabled(c.internalCustomer))
  ), [customerMaster]);

  /*
   * ----------------------------------------------------------
   * CONDITIONAL STATES
   * ----------------------------------------------------------
   */

  const isInternalProject = project.projectType === "Internal";
  const isNonBillable = project.projectBillability === "No";
  const disableCommercialFields = isInternalProject || isNonBillable;
  const disablePOSOWFields = isInternalProject;
  const showDoubleCreditTo = project.doubleCreditApplicable === "Yes";
  const showAbandonedReason = project.projectStatus === "Abandoned";
  const showClosureChecklist = project.projectStatus === "Completed";
  const canAddResources = isSubmitted && project.projectStatus === "In progress";
  const isDraftEdit = isEditing && project.projectStatus === "Draft";

  // FIX 5: Non-Draft projects are read-only (except status and abandoned reason)
  const isReadOnly = isSubmitted && project.projectStatus !== "Draft" && isEditing;
  const disableDraftEditableCommercialFields = isReadOnly || (!isDraftEdit && disableCommercialFields);
  const disableDraftEditablePOSOWFields = isReadOnly || (!isDraftEdit && disablePOSOWFields);
  const disableDraftEditableOwnership = isReadOnly || (!isDraftEdit && !project.projectType);
  const disableDraftEditableDepartment = isReadOnly || (!isDraftEdit && !project.division);

  // Load closure checklist when project status is Completed
  useEffect(() => {
    if (showClosureChecklist && (project.id || project.projectId)) {
      const pid = project.projectId || project.id;
      projectsAPI.getClosureChecklist(pid).then((res) => {
        setClosureChecklist(res.data || []);
      }).catch(() => {});
    }
  }, [showClosureChecklist, project.projectId, project.id]);

  // Save closure checklist (debounced)
  const saveClosureChecklistDebounced = useCallback(async (items) => {
    const pid = project.projectId || project.id;
    if (!pid) return;
    try {
      await projectsAPI.saveClosureChecklist(pid, items);
    } catch (err) {
      console.error("Failed to save closure checklist:", err);
    }
  }, [project.projectId, project.id]);

  /*
   * ----------------------------------------------------------
   * GENERIC PROJECT FIELD HANDLER
   * ----------------------------------------------------------
   */

  const handleChange = (field, value) => {
    setProject((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCurrencyChange = (value) => {
  setProject((prev) => ({
    ...prev,
    currency: value,
    inrConversionRate:
      value === "INR"
        ? 1
        : (Number(prev.inrConversionRate) > 1
            ? prev.inrConversionRate
            : ""),
  }));
};

  // INR projects always use a 1:1 conversion rate (read-only in the form).
  useEffect(() => {
    if (project.currency === "INR" && Number(project.inrConversionRate) !== 1) {
      setProject((prev) => ({
        ...prev,
        inrConversionRate: 1,
      }));
    }
  }, [project.currency, project.inrConversionRate]);

  const handleMilestoneChange = (index, field, value) => {
    setProject((prev) => ({
      ...prev,
      milestones: (prev.milestones && prev.milestones.length
        ? prev.milestones
        : EMPTY_MILESTONES.map((m) => ({ ...m }))
      ).map((m, i) => (i === index ? { ...m, [field]: value } : m)),
    }));
  };

  const handleBillingFrequencyChange = (value) => {
    setProject((prev) => ({
      ...prev,
      billingFrequency: value,
      ...(value !== "Milestone dates" ? { milestones: EMPTY_MILESTONES.map((m) => ({ ...m })) } : {}),
    }));
  };

  const handleBUChange = (value) => {
    const billingCompany = getBillingCompanyForBU(value);
    setProject((prev) => ({
      ...prev,
      businessUnit: value,
      utsBillingEntity: billingCompany || prev.utsBillingEntity,
      division: "",
      department: "",
      doubleCreditTo: "",
    }));
    setFilteredDivisionsByBU([]);
    setFilteredDepartmentsByDivBU([]);
    setFilteredDepartments([]);
    if (!value) return;
    const requestId = ++buRequestRef.current;
    headcountAPI.getDivisionsByBU(value)
      .then((res) => {
        if (requestId !== buRequestRef.current) return;
        setFilteredDivisionsByBU(res.data.divisions || []);
      })
      .catch(() => {
        if (requestId !== buRequestRef.current) return;
        setFilteredDivisionsByBU([]);
      });
  };

  const handleDivisionChange = (value) => {
    setProject((prev) => ({
      ...prev,
      division: value,
      department: "",
      doubleCreditTo: "",
    }));
  };

  /*
   * ----------------------------------------------------------
   * PROJECT TYPE CHANGE
   * ----------------------------------------------------------
   */

  const handleProjectTypeChange = (value) => {
    setProject((prev) => {
      const next = {
        ...prev,
        projectType: value,
        projectOwnershipModel: "",
        commercialModel: "",
        billingFrequency: "",
        poSowWoModel: "",
        customerPoSowAvailable: "",
        projectPoSowRef: "",
        poSowStartDate: "",
        poSowEndDate: "",
        doubleCreditTo: "",
      };
      // Internal projects are auto-configured: non-billable, executed at UTS
      // Office, and double credit not applicable.
      if (value === "Internal") {
        next.projectBillability = "No";
        next.projectExecutionAt = "UTS Office";
        next.doubleCreditApplicable = "No";
      }
      return next;
    });
  };

  const handleCustomerTypeChange = (value) => {
    setProject((prev) => ({
      ...prev,
      customerType: value,
      ...(value === "Internal"
        ? { endCustomer: "", endCustomerId: null, billingCustomer: "", billingCustomerId: null }
        : { internalCustomerId: null }),
    }));
  };

  /*
   * ----------------------------------------------------------
   * BILLABILITY CHANGE
   * ----------------------------------------------------------
   */

  const handleBillabilityChange = (value) => {
    setProject((prev) => ({
      ...prev,
      projectBillability: value,
      ...(value === "No"
        ? { commercialModel: "", billingFrequency: "", milestones: EMPTY_MILESTONES.map((m) => ({ ...m })) }
        : {}),
    }));
  };

  /*
   * ----------------------------------------------------------
   * DOUBLE CREDIT CHANGE
   * ----------------------------------------------------------
   */

  const handleDoubleCreditChange = (value) => {
    setProject((prev) => ({
      ...prev,
      doubleCreditApplicable: value,
      doubleCreditTo: "",
    }));
  };

  /*
   * ----------------------------------------------------------
   * PROJECT STATUS CHANGE
   * ----------------------------------------------------------
   */

  const handleProjectStatusChange = (value) => {
    setProject((prev) => ({
      ...prev,
      projectStatus: value,
      abandonedReason: value === "Abandoned" ? prev.abandonedReason : "",
    }));
  };

  /*
   * ----------------------------------------------------------
   * FILE ATTACHMENT
   * ----------------------------------------------------------
   */

  const ALLOWED_ATTACHMENT_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "image/jpeg",
    "image/png",
  ];

  const MAX_ATTACHMENT_SIZE = 20 * 1024 * 1024;

  const handleAttachmentChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      toast.error("Only PDF, DOC, DOCX, XLS, XLSX, JPG, PNG files are allowed");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_ATTACHMENT_SIZE) {
      toast.error("File size must be under 20 MB");
      event.target.value = "";
      return;
    }

    setProject((prev) => ({
      ...prev,
      attachment: file,
      attachmentName: file.name,
    }));
    event.target.value = "";
  };

  const uploadPendingAttachment = async (projectId) => {
    if (!project.attachment) return true;

    setAttachmentUploading(true);
    try {
      const formData = new FormData();
      formData.append("attachment", project.attachment);
      const res = await projectsAPI.uploadAttachment(projectId, formData);
      setProject((prev) => ({
        ...prev,
        attachment: null,
        attachmentUrl: res.data.attachmentUrl,
        attachmentName: res.data.attachmentName,
      }));
      return true;
    } catch (err) {
      console.error("Attachment upload error:", err);
      const msg = getErrorMessage(err, "Failed to upload attachment");
      toast.error(msg);
      return false;
    } finally {
      setAttachmentUploading(false);
    }
  };

  const handleRemoveAttachment = async () => {
    if (!project.id) {
      setProject((prev) => ({
        ...prev,
        attachment: null,
        attachmentUrl: null,
        attachmentName: "",
      }));
      return;
    }

    try {
      await projectsAPI.deleteAttachment(project.id);
      setProject((prev) => ({
        ...prev,
        attachment: null,
        attachmentUrl: null,
        attachmentName: "",
      }));
      toast.success("Attachment removed");
    } catch (err) {
      console.error("Remove attachment error:", err);
      toast.error("Failed to remove attachment");
    }
  };

  const handleViewAttachment = () => {
    if (project.attachmentUrl) {
      window.open(project.attachmentUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleDownloadAttachment = () => {
    if (project.attachmentUrl) {
      const link = document.createElement("a");
      link.href = project.attachmentUrl;
      link.download = project.attachmentName || "attachment";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  /*
   * ----------------------------------------------------------
   * NAVIGATION: Create New
   * ----------------------------------------------------------
   */

  const handleCreateNew = () => {
    setProject(INITIAL_PROJECT);
    setResources([]);
    setIsSubmitted(false);
    setIsEditing(false);
    setShowResourceForm(false);
    setResourceForm(INITIAL_RESOURCE);
    setResourceCustomer({ customerId: null, customerName: "" });
    setView("create");
  };

  /*
   * ----------------------------------------------------------
   * NAVIGATION: Back to List
   * ----------------------------------------------------------
   */

  const handleBackToList = () => {
    setView("list");
    setProject(INITIAL_PROJECT);
    setResources([]);
    setIsSubmitted(false);
    setIsEditing(false);
    setShowResourceForm(false);
    setResourceCustomer({ customerId: null, customerName: "" });
  };

  /*
   * ----------------------------------------------------------
   * SAVE DRAFT
   * ----------------------------------------------------------
   */

  const refreshSavedProject = async (projectId) => {
    const res = await projectsAPI.getById(projectId);
    setProject(mapDbToProject(res.data));
    await fetchProjects();
  };

const handleSaveDraft = async () => {
  setFormLoading(true);

  try {
    const payload = mapProjectToApi(project);

    if (
      payload.currency !== "INR" &&
      (!Number.isFinite(payload.inrConversionRate) ||
        payload.inrConversionRate <= 0)
    ) {
      toast.error(`Enter a valid INR conversion rate for ${payload.currency}`);
      setFormLoading(false);
      return;
    }

    if (!isEditing) {
      payload.projectStatus = "Draft";
    }


      let projectId;

      if (isEditing && project.id) {
        await projectsAPI.update(project.id, payload);
        projectId = project.id;
        toast.success("Project draft updated");
      } else {
        const res = await projectsAPI.create(payload);
        projectId = res.data.id;
        setProject((prev) => ({
          ...prev,
          id: res.data.id,
          projectId: res.data.project_id,
        }));
        setIsEditing(true);
        toast.success("Project draft saved");
      }

      if (project.attachment) {
        const uploaded = await uploadPendingAttachment(projectId);
        if (!uploaded) {
          toast.warning("Project saved but attachment upload failed. You can retry from the edit form.");
        }
      }

      await refreshSavedProject(projectId);
    } catch (err) {
      console.error("Save draft error:", err);
      toast.error("Failed to save draft");
    } finally {
      setFormLoading(false);
    }
  };

  /*
   * ----------------------------------------------------------
   * SUBMIT PROJECT
   * ----------------------------------------------------------
   */

  const handleSubmit = async () => {
    setFormLoading(true);
    try {
      const payload = mapProjectToApi(project);

      if (
  payload.currency !== "INR" &&
  (!Number.isFinite(payload.inrConversionRate) ||
    payload.inrConversionRate <= 0)
) {
  toast.error(`Enter a valid INR conversion rate for ${payload.currency}`);
  setFormLoading(false);
  return;
}

      if (!isEditing) {
        payload.projectStatus = "Yet to start";
      }

      let projectId;

      if (isEditing && project.id) {
        await projectsAPI.update(project.id, payload);
        projectId = project.id;
        toast.success("Project updated successfully");
      } else {
        const res = await projectsAPI.create(payload);
        projectId = res.data.id;
        setProject((prev) => ({
          ...prev,
          id: res.data.id,
          projectId: res.data.project_id,
        }));
        setIsEditing(true);
        toast.success("Project submitted successfully");
      }

      if (project.attachment) {
        const uploaded = await uploadPendingAttachment(projectId);
        if (!uploaded) {
          toast.warning("Project saved but attachment upload failed. You can retry from the edit form.");
        }
      }

      await refreshSavedProject(projectId);

      setIsSubmitted(true);
      if (!isEditing) {
        setProject((prev) => ({
          ...prev,
          projectStatus: "Yet to start",
        }));
      }
    } catch (err) {
      console.error("Submit project error:", err);
      toast.error("Failed to submit project");
    } finally {
      setFormLoading(false);
    }
  };

  /*
   * ----------------------------------------------------------
   * VIEW EXISTING PROJECT (from list)
   * ----------------------------------------------------------
   */

  const handleViewProject = async (row) => {
    try {
      const res = await projectsAPI.getById(row.id);
      const mapped = mapDbToProject(res.data);
      setProject(mapped);
      setResourceCustomer({ customerId: null, customerName: "" });
      setResources((res.data.resources || []).map((r) => ({
        ...r,
        id: r.id,
        name: r.name,
        employeeId: r.employee_id || "",
        billable: r.billable,
        projectExecutionAt: r.project_execution_at,
        projectLocation: r.project_location,
        billingStartDate: r.billing_start_date,
        billingEndDate: r.billing_end_date,
        poSowWoRef: r.po_sow_wo_ref,
        poStartDate: r.po_start_date,
        poEndDate: r.po_end_date,
        poRole: r.po_role,
        billingRateType: r.billing_rate_type,
        billingRate: r.billing_rate,
        allocation: r.allocation,
        estimatedRevenuePerMonth: r.estimated_revenue_per_month,
        estimatedCostPerMonth: r.estimated_cost_per_month,
      })));
      setIsEditing(true);
      setIsSubmitted(true);
      setView("create");
    } catch (err) {
      console.error("Load project error:", err);
      toast.error("Failed to load project details");
    }
  };

  /*
   * ----------------------------------------------------------
   * EDIT EXISTING PROJECT (from list)
   * ----------------------------------------------------------
   */

  const handleEditProject = async (row) => {
    try {
      const res = await projectsAPI.getById(row.id);
      const mapped = mapDbToProject(res.data);
      setProject(mapped);
      setResourceCustomer({ customerId: null, customerName: "" });
      setResources((res.data.resources || []).map((r) => ({
        ...r,
        id: r.id,
        name: r.name,
        employeeId: r.employee_id || "",
        billable: r.billable,
        projectExecutionAt: r.project_execution_at,
        projectLocation: r.project_location,
        billingStartDate: r.billing_start_date,
        billingEndDate: r.billing_end_date,
        poSowWoRef: r.po_sow_wo_ref,
        poStartDate: r.po_start_date,
        poEndDate: r.po_end_date,
        poRole: r.po_role,
        billingRateType: r.billing_rate_type,
        billingRate: r.billing_rate,
        allocation: r.allocation,
        estimatedRevenuePerMonth: r.estimated_revenue_per_month,
        estimatedCostPerMonth: r.estimated_cost_per_month,
      })));
      setIsEditing(true);
      setIsSubmitted(mapped.projectStatus !== "Draft");
      setView("create");
    } catch (err) {
      console.error("Load project for edit error:", err);
      toast.error("Failed to load project for editing");
    }
  };

  /*
   * ----------------------------------------------------------
   * DELETE PROJECT
   * ----------------------------------------------------------
   */

  const handleDeleteClick = (row) => {
    setProjectToDelete(row);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!projectToDelete) return;
    setDeleteLoading(true);
    try {
      await projectsAPI.delete(projectToDelete.id);
      toast.success("Project deleted successfully");
      setShowDeleteModal(false);
      setProjectToDelete(null);
      fetchProjects();
    } catch (err) {
      console.error("Delete project error:", err);
      toast.error("Failed to delete project");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  /*
   * ============================================================
   * RESOURCE OPERATIONS
   * ============================================================
   */

  const handleResourceChange = (field, value) => {
    setResourceForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Open Add Resource: project_id identifies the project; its Customer Master
  // customer_id is fetched from the backend for Rate Master customer-level lookup.
  const openAddResource = async () => {
    setEmployeeAllocation({ totalAllocated: 0, projectCount: 0, loading: false });
    setResourceForm((prev) => ({
      ...prev,
      projectExecutionAt: project.projectExecutionAt || "",
      projectLocation: project.projectLocation || "",
    }));
    setShowResourceForm(true);

    setResourceCustomer({ customerId: null, customerName: "" });
    if (!project.id) return;

    try {
      const res = await projectsAPI.getCustomerId(project.id);
      setResourceCustomer({
        customerId: res.data.customerId || null,
        customerName: res.data.customerName || "",
      });
    } catch {
      // Fall back to the project record already loaded from the backend
      setResourceCustomer({
        customerId: project.customerType === "Internal" ? project.internalCustomerId || null : project.endCustomerId || null,
        customerName: project.customerType === "Internal" ? (customerMaster.find((c) => String(c.customerId) === String(project.internalCustomerId))?.customerName || "") : project.endCustomer || "",
      });
    }
  };

  const handleAddResource = async () => {
    if (!project.id) {
      toast.error("Please save the project first before adding resources");
      return;
    }

    // Validation: role-based billing requires a matching rate from Rate Master
    if (isRoleBasedPO) {
      if (!resourceForm.poRole) {
        toast.error("PO Role is required for role-based billing");
        return;
      }
      if (!resourceForm.billingRateType) {
        toast.error("Billing Rate Type is required for role-based billing");
        return;
      }
      if (!resourceForm.billingRate || Number(resourceForm.billingRate) <= 0) {
        toast.error("No matching billing rate found. Add the rate in Rate Master tab first.");
        return;
      }
    }

    // Validation: allocation is per project (0%–100%). Combined allocation
    // across projects is NOT limited to 100%.
    if (resourceForm.employeeId) {
      const allocValue =
        resourceForm.allocation === "" || resourceForm.allocation === null
          ? Number.NaN
          : Number(resourceForm.allocation);
      if (Number.isNaN(allocValue)) {
        toast.error("Allocation must be between 0% and 100%");
        return;
      }
      if (allocValue < 0) {
        toast.error("Allocation cannot be negative");
        return;
      }
      if (allocValue > 100) {
        toast.error("Allocation cannot exceed 100% for a single project");
        return;
      }
    }

    try {
      const res = await projectsAPI.addResource(project.id, resourceForm);
      setResources((prev) => [
        ...prev,
        {
          ...res.data,
          id: res.data.id,
          name: res.data.name,
          employeeId: res.data.employee_id || "",
          billable: res.data.billable,
          projectExecutionAt: res.data.project_execution_at,
          projectLocation: res.data.project_location,
          billingStartDate: res.data.billing_start_date,
          billingEndDate: res.data.billing_end_date,
          poSowWoRef: res.data.po_sow_wo_ref,
          poStartDate: res.data.po_start_date,
          poEndDate: res.data.po_end_date,
          poRole: res.data.po_role,
          billingRateType: res.data.billing_rate_type,
          billingRate: res.data.billing_rate,
          allocation: res.data.allocation,
          estimatedRevenuePerMonth: res.data.estimated_revenue_per_month,
          estimatedCostPerMonth: res.data.estimated_cost_per_month,
        },
      ]);
      setResourceForm(INITIAL_RESOURCE);
      setEmployeeAllocation({ totalAllocated: 0, projectCount: 0, loading: false });
      setResourceCustomer({ customerId: null, customerName: "" });
      setRateLookup({ found: false, rate: null, loading: false, searched: false });
      setShowResourceForm(false);
      toast.success("Resource added");
    } catch (err) {
      console.error("Add resource error:", err);
      toast.error(getErrorMessage(err, "Failed to add resource"));
    }
  };

  const handleViewResource = (resource) => {
    setResourceToView(resource);
  };

  const handleDeleteResource = async (resourceId) => {
    if (!project.id) return;
    try {
      await projectsAPI.deleteResource(project.id, resourceId);
      setResources((prev) => prev.filter((r) => r.id !== resourceId));
      toast.success("Resource removed");
    } catch (err) {
      console.error("Delete resource error:", err);
      toast.error("Failed to remove resource");
    }
  };

  /*
   * ============================================================
   * RENDER: PROJECT LIST
   * ============================================================
   */

  if (view === "list") {
    return (
      <div className="projects-tab">

        {/* PAGE HEADER */}
        <div className="projects-header">
          <div>
            <h2>Projects</h2>
            <p>Manage project information and resources</p>
          </div>

          {canCreateProject && (
            <button
              type="button"
              className="pj-btn-primary"
              onClick={handleCreateNew}
            >
              + Create Project
            </button>
          )}
        </div>

        {/* SEARCH & FILTERS */}
        <div className="card">
          <div className="search-header">
            <h3>All Projects ({total})</h3>
            <div className="search-container">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by Project ID, name, or customer..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
                {search && (
                  <button className="clear-search-btn" type="button" onClick={() => { setSearch(""); setPage(1); }}>Clear</button>
                )}
              </div>
            </div>
          </div>

          <div className="filter-toolbar" ref={filterToolbarRef}>
            <div className="filter-toolbar-left">
              {renderFilterDropdown({
                label: "Status",
                allLabel: "All Statuses",
                values: PROJECT_STATUS_OPTIONS,
                value: statusFilter,
                onSelect: (v) => { setStatusFilter(v || ""); closeAllDropdowns(); setPage(1); },
                open: showStatusDropdown,
                onToggle: () => { closeAllDropdowns(); setShowStatusDropdown(!showStatusDropdown); },
              })}
              {renderFilterDropdown({
                label: "Type",
                allLabel: "All Types",
                values: PROJECT_TYPE_OPTIONS,
                value: typeFilter,
                onSelect: (v) => { setTypeFilter(v || ""); closeAllDropdowns(); setPage(1); },
                open: showTypeDropdown,
                onToggle: () => { closeAllDropdowns(); setShowTypeDropdown(!showTypeDropdown); },
              })}
              {renderFilterDropdown({
                label: "Division",
                allLabel: "All Divisions",
                values: dropdownOptions.divisions,
                value: divisionFilter,
                onSelect: (v) => { setDivisionFilter(v || ""); closeAllDropdowns(); setPage(1); },
                open: showDivisionDropdown,
                onToggle: () => { closeAllDropdowns(); setShowDivisionDropdown(!showDivisionDropdown); },
              })}
              {renderFilterDropdown({
                label: "Location",
                allLabel: "All Locations",
                values: PROJECT_LOCATION_OPTIONS,
                value: locationFilter,
                onSelect: (v) => { setLocationFilter(v || ""); closeAllDropdowns(); setPage(1); },
                open: showLocationDropdown,
                onToggle: () => { closeAllDropdowns(); setShowLocationDropdown(!showLocationDropdown); },
              })}
              {renderFilterDropdown({
                label: "Billable",
                allLabel: "All Billability",
                values: YES_NO_OPTIONS,
                value: billabilityFilter,
                onSelect: (v) => { setBillabilityFilter(v || ""); closeAllDropdowns(); setPage(1); },
                open: showBillabilityDropdown,
                onToggle: () => { closeAllDropdowns(); setShowBillabilityDropdown(!showBillabilityDropdown); },
              })}
              {renderFilterDropdown({
                label: "Ownership",
                allLabel: "All Ownership",
                values: ownershipOptionsFlat,
                value: ownershipFilter,
                onSelect: (v) => { setOwnershipFilter(v || ""); closeAllDropdowns(); setPage(1); },
                open: showOwnershipDropdown,
                onToggle: () => { closeAllDropdowns(); setShowOwnershipDropdown(!showOwnershipDropdown); },
              })}
            </div>
            {hasActiveFilters && (
              <button type="button" className="btn outline filter-reset-btn" onClick={clearAllFilters}>
                Clear Filters
              </button>
            )}
          </div>

        {/* PROJECT TABLE */}
        <div className="projects-table-wrapper">
          {listLoading ? (
            <div className="projects-loading">
              <div className="pj-spinner" />
              <p>Loading projects...</p>
            </div>
          ) : projects.length === 0 ? (
            <div className="projects-empty">
              <div className="empty-icon">&#128194;</div>
              <h3>No projects found</h3>
              <p>
                {hasActiveFilters
                  ? "Try adjusting your search or filters."
                  : "Click \"+ Create Project\" to get started."}
              </p>
              {!hasActiveFilters && canCreateProject && (
                <button
                  type="button"
                  className="pj-btn-primary"
                  onClick={handleCreateNew}
                >
                  + Create Project
                </button>
              )}
            </div>
          ) : (
            <>
              <table className="projects-table">
                <thead>
                  <tr>
                    <th scope="col">Project ID</th>
                    <th scope="col">Description</th>
                    <th scope="col">End Customer</th>
                    <th scope="col">Project Type</th>
                    <th scope="col">Ownership</th>
                    <th scope="col">Status</th>
                    <th scope="col">Location</th>
                    <th scope="col">Billable</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((row) => (
                    <tr key={row.id}>
                      <td className="project-id-cell">{row.project_id}</td>
                      <td className="description-cell">
                        {row.description || "—"}
                      </td>
                      <td>{row.end_customer || "—"}</td>
                      <td>{row.project_type || "—"}</td>
                      <td>{row.project_ownership_model || "—"}</td>
                      <td>
                        <span className={`status-badge ${getStatusClass(row.project_status)}`}>
                          {row.project_status}
                        </span>
                      </td>
                      <td>{row.project_location || "—"}</td>
                      <td>{row.project_billability || "—"}</td>
                      <td className="actions-cell">
                        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                          <button type="button" className="btn outline" onClick={() => handleViewProject(row)}>View</button>
                          {canEditProject && (
                            <button type="button" className="btn primary" onClick={() => handleEditProject(row)}>Edit</button>
                          )}
                          {canDeleteProject && (
                            <button type="button" className="btn danger" onClick={() => handleDeleteClick(row)}>Delete</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* PAGINATION */}
              {total > limit && (
                <div className="projects-pagination">
                  <span>
                    Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                  </span>
                  <div className="pj-pagination-buttons">
                    <button
                      type="button"
                      className="pj-btn-secondary"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.ceil(total / limit) }, (_, index) => index + 1).map((pageNumber) => (
                      <button
                        type="button"
                        key={pageNumber}
                        className={pageNumber === page ? "pj-page-number active" : "pj-page-number"}
                        onClick={() => setPage(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="pj-btn-secondary"
                      disabled={page * limit >= total}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          </div>
        </div>

        {/* DELETE CONFIRMATION MODAL */}
        {showDeleteModal && (
          <div className="pj-modal-overlay" onClick={handleCancelDelete}>
            <div className="pj-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Project</h3>
              <p>
                Are you sure you want to delete project{" "}
                <strong>{projectToDelete?.project_id}</strong>?
                This action cannot be undone.
              </p>
              <div className="pj-modal-actions">
                <button
                  type="button"
                  className="pj-btn-secondary"
                  onClick={handleCancelDelete}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pj-btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /*
   * ============================================================
   * RENDER: CREATE / EDIT PROJECT
   * ============================================================
   */

  return (
    <div className="projects-tab project-form-page">

      {/* PAGE HEADER */}
      <div className="projects-header project-form-header">
        <div>
          <nav className="project-form-breadcrumb" aria-label="Breadcrumb"><span>Projects</span><span aria-hidden="true">›</span><strong>Project Details</strong></nav>
          <h2>Project Details</h2>
          <p>Create or update project information and configure all required details.</p>
        </div>

        <div className="project-form-top-actions">
          <button
            type="button"
            className="pj-btn-secondary project-form-cancel"
            onClick={handleBackToList}
          >
            Cancel
          </button>
          {(canCreateProject || canEditProject) && <button type="button" className="project-form-save-draft" onClick={handleSaveDraft} disabled={formLoading}>{formLoading ? "Saving..." : "Save Draft"}</button>}
          {(canCreateProject || canEditProject) && <button type="button" className="project-form-save" onClick={handleSubmit} disabled={formLoading}>{formLoading ? "Saving..." : "Save"}</button>}
        </div>
      </div>


      {/* =====================================================
          CREATE PROJECT
          ===================================================== */}

      <section className="project-section project-form-card">

        <div className="section-header">
          <h3>Project Overview</h3>
        </div>


        {/* =================================================
            BASIC PROJECT INFORMATION
            ================================================= */}

        <div className="pj-form-grid pj-form-grid-overview">

          {/* Project ID */}
          <div className="pj-form-group">
            <label>Project ID</label>
            <input
              type="text"
              value={project.projectId}
              placeholder="Auto generated"
              onChange={(e) => handleChange("projectId", e.target.value)}
              disabled={!isDraftEdit}
              readOnly
            />
          </div>

          {/* Description */}
          <div className="pj-form-group pj-overview-description">
            <label>Description of Project <span className="pj-required-marker">*</span></label>
            <textarea
              maxLength={200}
              value={project.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Enter full description of the project"
              disabled={isReadOnly}
            />
            <span className="project-description-counter">{String(project.description || "").length}/200 characters</span>
          </div>

          {/* Customer Type */}
          <div className="pj-form-group">
            <label>Customer Type <span className="pj-required-marker">*</span></label>
            <select value={project.customerType || "External"} onChange={(e) => handleCustomerTypeChange(e.target.value)} disabled={isReadOnly}>
              <option value="External">External</option>
              <option value="Internal">Internal</option>
            </select>
          </div>

          {/* Internal Customer */}
          {project.customerType === "Internal" && (
            <div className="pj-form-group">
              <label>Internal Customer <span className="pj-required-marker">*</span></label>
              <SearchableSelect
                value={project.internalCustomerId || ""}
                options={sortedOptions(internalCustomerOptions, (c) => `${c.customerId} - ${c.customerName}`)}
                getOptionValue={(customer) => customer.customerId}
                getOptionLabel={(customer) => `${customer.customerId} - ${customer.customerName}`}
                selectedDisplayValue={project.internalCustomerId ? `${project.internalCustomerId} - ${customerMaster.find((c) => String(c.customerId) === String(project.internalCustomerId))?.customerName || project.internalCustomerId}` : ""}
                placeholder="Select Internal Customer"
                onChange={(id) => handleChange("internalCustomerId", id)}
                disabled={isReadOnly}
              />
            </div>
          )}

          {project.customerType !== "Internal" && (
            <>
              {/* End Customer */}
              <div className="pj-form-group">
                <label>End Customer <span className="pj-required-marker">*</span></label>
                <SearchableSelect
                  value={project.endCustomerId || ""}
                  options={sortedOptions(endCustomerOptions, (c) => `${c.customerId} - ${c.customerName}`)}
                  getOptionValue={(customer) => customer.customerId}
                  getOptionLabel={(customer) => `${customer.customerId} - ${customer.customerName}`}
                  selectedDisplayValue={project.endCustomerId ? `${project.endCustomerId} - ${customerMaster.find((c) => String(c.customerId) === String(project.endCustomerId))?.customerName || project.endCustomer || project.endCustomerId}` : ""}
                  placeholder="Select End Customer"
                  onChange={(id) => {
                    const sel = endCustomerOptions.find((c) => c.customerId === id);
                    handleChange("endCustomerId", id);
                    handleChange("endCustomer", sel ? sel.customerName : "");
                  }}
                  disabled={isReadOnly}
                />
              </div>

              {/* Billing Customer */}
              <div className="pj-form-group">
                <label>Billing Customer <span className="pj-required-marker">*</span></label>
                <SearchableSelect
                  value={project.billingCustomerId || ""}
                  options={sortedOptions(billingCustomerOptions, (c) => `${c.customerId} - ${c.customerName}`)}
                  getOptionValue={(customer) => customer.customerId}
                  getOptionLabel={(customer) => `${customer.customerId} - ${customer.customerName}`}
                  selectedDisplayValue={project.billingCustomerId ? `${project.billingCustomerId} - ${customerMaster.find((c) => String(c.customerId) === String(project.billingCustomerId))?.customerName || project.billingCustomer || project.billingCustomerId}` : ""}
                  placeholder="Select Billing Customer"
                  onChange={(id) => {
                    const sel = billingCustomerOptions.find((c) => c.customerId === id);
                    handleChange("billingCustomerId", id);
                    handleChange("billingCustomer", sel ? sel.customerName : "");
                  }}
                  disabled={isReadOnly}
                />
              </div>
            </>
          )}

          {/* Project Type */}
          <div className="pj-form-group">
            <label>Project Type <span className="pj-required-marker">*</span></label>
            <select
              value={project.projectType}
              onChange={(e) => handleProjectTypeChange(e.target.value)}
              disabled={isReadOnly}
            >
              <option value="">Select Project Type</option>
              {sortedOptions(PROJECT_TYPE_OPTIONS).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {/* Project Ownership */}
          <div className="pj-form-group">
            <label>Project Ownership Model <span className="pj-required-marker">*</span></label>
            <select
              value={project.projectOwnershipModel}
              onChange={(e) => handleChange("projectOwnershipModel", e.target.value)}
              disabled={disableDraftEditableOwnership}
            >
              <option value="">Select Ownership Model</option>
              {sortedOptions(ownershipOptions).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

        </div>
      </section>

        {/* =================================================
            MILESTONE BILLING (only when "Milestone dates")
            ================================================= */}
      <section className="project-section project-form-card">
        <div className="section-header project-form-section-header"><h3>Business and Delivery Details</h3></div>
        {project.billingFrequency === "Milestone dates" && (
          <div className="pj-milestones-section">
            <div className="pj-milestones-header">
              <h4>Milestone Billing Details</h4>
              <p>Add up to 4 milestones with a description, expected date and amount.</p>
            </div>
            <div className="pj-milestones-grid">
              {(project.milestones && project.milestones.length
                ? project.milestones
                : EMPTY_MILESTONES
              ).map((m, idx) => (
                <div key={idx} className="pj-milestone-card">
                  <h5>Milestone {idx + 1}</h5>
                  <div className="pj-form-group">
                    <label>Description</label>
                    <input
                      type="text"
                      value={m.description || ""}
                      onChange={(e) => handleMilestoneChange(idx, "description", e.target.value)}
                      disabled={isReadOnly}
                      placeholder="Description"
                    />
                  </div>
                  <div className="pj-form-group">
                    <label>Date</label>
                    <input
                      type="date"
                      value={m.date || ""}
                      onChange={(e) => handleMilestoneChange(idx, "date", e.target.value)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="pj-form-group">
                    <label>Amount</label>
                    <input
                      type="number"
                      value={m.amount || ""}
                      onChange={(e) => handleMilestoneChange(idx, "amount", e.target.value)}
                      disabled={isReadOnly}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* =================================================
            BUSINESS AND DELIVERY DETAILS
            ================================================= */}

        <div className="pj-form-grid">
          <div className="pj-form-group">
            <label>Project Billability <span className="pj-required-marker">*</span></label>
            <select
              value={project.projectBillability}
              onChange={(e) => handleBillabilityChange(e.target.value)}
              disabled={isReadOnly}
            >
              <option value="">Select</option>
              {sortedOptions(YES_NO_OPTIONS).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="pj-form-group">
            <label>Commercial Model <span className="pj-required-marker">*</span></label>
            <select
              value={project.commercialModel}
              onChange={(e) => handleChange("commercialModel", e.target.value)}
              disabled={disableDraftEditableCommercialFields}
            >
              <option value="">Select Commercial Model</option>
              {sortedOptions(COMMERCIAL_MODEL_OPTIONS).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="pj-form-group">
            <label>Billing Frequency <span className="pj-required-marker">*</span></label>
            <select
              value={project.billingFrequency}
              onChange={(e) => handleBillingFrequencyChange(e.target.value)}
              disabled={disableDraftEditableCommercialFields}
            >
              <option value="">Select Billing Frequency</option>
              {sortedOptions(BILLING_FREQUENCY_OPTIONS).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="pj-form-group">
            <label>Project Execution At <span className="pj-required-marker">*</span></label>
            <select
              value={project.projectExecutionAt}
              onChange={(e) => handleChange("projectExecutionAt", e.target.value)}
              disabled={isReadOnly}
            >
              <option value="">Select</option>
              {sortedOptions(PROJECT_EXECUTION_OPTIONS).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="pj-form-grid">
          <div className="pj-form-group">
            <label>Project Location <span className="pj-required-marker">*</span></label>
            <select
              value={project.projectLocation}
              onChange={(e) => handleChange("projectLocation", e.target.value)}
              disabled={isReadOnly}
            >
              <option value="">Select Location</option>
              {sortedOptions(PROJECT_LOCATION_OPTIONS).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="pj-form-group">
            <label>Industry Vertical <span className="pj-required-marker">*</span></label>
            <SearchableSelect
              value={project.industryVertical}
              options={sortedOptions(INDUSTRY_VERTICAL_OPTIONS)}
              getOptionValue={(option) => option}
              getOptionLabel={(option) => option}
              placeholder="Select Industry"
              onChange={(value) => handleChange("industryVertical", value)}
              disabled={disableDraftEditableCommercialFields}
            />
          </div>

          <div className="pj-form-group">
            <label>UTS Billing Company <span className="pj-required-marker">*</span></label>
            <select
              value={project.utsBillingEntity}
              onChange={(e) => handleChange("utsBillingEntity", e.target.value)}
              disabled={isReadOnly}
            >
              <option value="">Select Billing Entity</option>
              {sortedOptions(dropdownOptions.companies).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="pj-form-group">
            <label>Business Unit <span className="pj-required-marker">*</span></label>
            <select
              value={project.businessUnit || ""}
              onChange={(e) => handleBUChange(e.target.value)}
              disabled={isReadOnly}
            >
              <option value="">Select Business Unit</option>
              {sortedOptions(buOptions).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="pj-form-grid">
          <div className="pj-form-group">
            <label>Division <span className="pj-required-marker">*</span></label>
            <select
              value={project.division}
              onChange={(e) => handleDivisionChange(e.target.value)}
              disabled={isReadOnly || !project.businessUnit}
            >
              <option value="">{project.businessUnit ? "Select Division" : "Select Business Unit first"}</option>
              {sortedOptions(filteredDivisionsByBU.length > 0 ? filteredDivisionsByBU : (project.businessUnit ? [] : dropdownOptions.divisions)).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="pj-form-group">
            <label>Department <span className="pj-required-marker">*</span></label>
            <select
              value={project.department}
              onChange={(e) => handleChange("department", e.target.value)}
              disabled={isReadOnly || !project.division}
            >
              <option value="">{project.division ? "Select Department" : "Select Division first"}</option>
              {sortedOptions(filteredDepartmentsByDivBU.length > 0 ? filteredDepartmentsByDivBU : filteredDepartments).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

        {/* =================================================
            CONTRACT AND FINANCIAL DETAILS
            ================================================= */}
      <section className="project-section project-form-card">
        <div className="section-header project-form-section-header"><h3>Contract and Financial Details</h3></div>
        <div className="pj-form-grid">
          <div className="pj-form-group">
            <label>PO / SOW / WO Model <span className="pj-required-marker">*</span></label>
            <select
              value={project.poSowWoModel}
              onChange={(e) => handleChange("poSowWoModel", e.target.value)}
              disabled={disableDraftEditablePOSOWFields}
            >
              <option value="">Select Model</option>
              {sortedOptions(PO_SOW_MODEL_OPTIONS).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="pj-form-group">
            <label>Customer PO / SOW Available? <span className="pj-required-marker">*</span></label>
            <select
              value={project.customerPoSowAvailable}
              onChange={(e) => handleChange("customerPoSowAvailable", e.target.value)}
              disabled={disableDraftEditablePOSOWFields}
            >
              <option value="">Select</option>
              {sortedOptions(CUSTOMER_PO_OPTIONS).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="pj-form-group">
            <label>Project PO / SOW Ref</label>
            <input
              type="text"
              value={project.projectPoSowRef}
              onChange={(e) => handleChange("projectPoSowRef", e.target.value)}
              disabled={disableDraftEditablePOSOWFields}
            />
          </div>

          <div className="pj-form-group">
            <label>PO / SOW Start Date <span className="pj-required-marker">*</span></label>
            <input
              type="date"
              value={project.poSowStartDate}
              onChange={(e) => handleChange("poSowStartDate", e.target.value)}
              disabled={disableDraftEditablePOSOWFields}
            />
          </div>
        </div>

        <div className="pj-form-grid">
          <div className="pj-form-group">
            <label>PO / SOW End Date <span className="pj-required-marker">*</span></label>
            <input
              type="date"
              value={project.poSowEndDate}
              onChange={(e) => handleChange("poSowEndDate", e.target.value)}
              disabled={disableDraftEditablePOSOWFields}
            />
          </div>

          <div className="pj-form-group">
            <label>PO / SOW Value <span className="pj-required-marker">*</span></label>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <select
                value={project.currency}
                onChange={(e) => handleCurrencyChange(e.target.value)}
                style={{ width: "88px", flexShrink: 0 }}
                disabled={disableDraftEditablePOSOWFields}
              >
                {sortedOptions(CURRENCY_OPTIONS).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <input
                placeholder="Amount"
                type="number"
                min="0"
                value={project.poSowValue}
                onChange={(e) => handleChange("poSowValue", e.target.value)}
                style={{ flex: 1 }}
                disabled={disableDraftEditablePOSOWFields}
              />
            </div>
          </div>

          <div className="pj-form-group">
            <label>INR Conversion Rate <span className="pj-required-marker">*</span></label>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span style={{ color: "#102A56", fontSize: "13px", whiteSpace: "nowrap" }}>{inrPerLabel(project.currency)}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={project.inrConversionRate}
                onChange={(e) => handleChange("inrConversionRate", e.target.value)}
                disabled={disableDraftEditablePOSOWFields || project.currency === "INR"}
                style={{ flex: 1 }}
              />
            </div>
          </div>

          <div className="pj-form-group">
            <label>Attachment</label>
            {project.attachmentUrl && !project.attachment ? (
              <div className="attachment-display">
                <span className="attachment-filename" title={project.attachmentName}>{project.attachmentName}</span>
                <div className="attachment-actions">
                  <button type="button" className="btn-attachment" onClick={handleViewAttachment}>View</button>
                  <button type="button" className="btn-attachment" onClick={handleDownloadAttachment}>Download</button>
                  <button type="button" className="btn-attachment" onClick={handleRemoveAttachment}>Remove</button>
                </div>
              </div>
            ) : project.attachment ? (
              <div className="attachment-display attachment-pending">
                <span className="attachment-filename" title={project.attachment.name}>{project.attachment.name}</span>
                <div className="attachment-actions">
                  <button type="button" className="btn-attachment" onClick={() => setProject((prev) => ({ ...prev, attachment: null, attachmentName: prev.attachmentUrl ? prev.attachmentName : "" }))}>Remove</button>
                </div>
              </div>
            ) : (
              <label className="attachment-upload-label">
                <input type="file" onChange={handleAttachmentChange} className="attachment-input" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" />
                <span className="attachment-upload-text">Choose file</span>
              </label>
            )}
            {attachmentUploading && <span className="pj-field-hint">Uploading...</span>}
          </div>
        </div>
        <p className="project-form-required-note"><span aria-hidden="true">i</span>Fields marked with <strong>*</strong> are mandatory</p>
      </section>
      <section className="project-section project-form-card">
        <div className="section-header project-form-section-header"><h3>Project Administration</h3></div>
        {/* =================================================
            PROJECT OWNERS
            ================================================= */}

        <div className="sub-section">
          <h4>Project Owners</h4>

          <div className="pj-form-grid">
            <div className="pj-form-group" ref={ownerDropdownRef} style={{ position: "relative" }}>
              <label>Delivery</label>
              <input
                type="text"
                value={ownerSearchField === "delivery" ? ownerSearchTerm : project.projectOwners.delivery}
                onChange={(e) => {
                  setOwnerSearchField("delivery");
                  setOwnerSearchTerm(e.target.value);
                  setShowOwnerDropdown(true);
                  setProject((prev) => ({
                    ...prev,
                    projectOwners: { ...prev.projectOwners, delivery: e.target.value, deliveryId: "" },
                  }));
                }}
                onFocus={() => {
                  setOwnerSearchField("delivery");
                  setOwnerSearchTerm("");
                  setShowOwnerDropdown(true);
                }}
                placeholder="Search employee"
                disabled={isReadOnly}
              />
              {showOwnerDropdown && ownerSearchField === "delivery" && ownerSearchResults.length > 0 && (
                <div className="pj-autocomplete-dropdown">
                  {sortedOptions(ownerSearchResults, (emp) => `${emp.employee_id} ${emp.name}`).map((emp) => (
                    <div
                      key={emp.employee_id}
                      className="pj-autocomplete-item"
                      onMouseDown={() => {
                        setProject((prev) => ({
                          ...prev,
                          projectOwners: { ...prev.projectOwners, delivery: emp.name, deliveryId: emp.employee_id },
                        }));
                        setOwnerSearchField("");
                        setOwnerSearchTerm("");
                        setShowOwnerDropdown(false);
                      }}
                    >
                      <strong>{emp.employee_id}</strong>
                      <span>{emp.name} ({emp.department || ""})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pj-form-group" style={{ position: "relative" }}>
              <label>Sales</label>
              <input
                type="text"
                value={ownerSearchField === "sales" ? ownerSearchTerm : project.projectOwners.sales}
                onChange={(e) => {
                  setOwnerSearchField("sales");
                  setOwnerSearchTerm(e.target.value);
                  setShowOwnerDropdown(true);
                  setProject((prev) => ({
                    ...prev,
                    projectOwners: { ...prev.projectOwners, sales: e.target.value, salesId: "" },
                  }));
                }}
                onFocus={() => {
                  setOwnerSearchField("sales");
                  setOwnerSearchTerm("");
                  setShowOwnerDropdown(true);
                }}
                placeholder="Search employee"
                disabled={isReadOnly}
              />
              {showOwnerDropdown && ownerSearchField === "sales" && ownerSearchResults.length > 0 && (
                <div className="pj-autocomplete-dropdown">
                  {sortedOptions(ownerSearchResults, (emp) => `${emp.employee_id} ${emp.name}`).map((emp) => (
                    <div
                      key={emp.employee_id}
                      className="pj-autocomplete-item"
                      onMouseDown={() => {
                        setProject((prev) => ({
                          ...prev,
                          projectOwners: { ...prev.projectOwners, sales: emp.name, salesId: emp.employee_id },
                        }));
                        setOwnerSearchField("");
                        setOwnerSearchTerm("");
                        setShowOwnerDropdown(false);
                      }}
                    >
                      <strong>{emp.employee_id}</strong>
                      <span>{emp.name} ({emp.department || ""})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pj-form-group" style={{ position: "relative" }}>
              <label>Operations</label>
              <input
                type="text"
                value={ownerSearchField === "operations" ? ownerSearchTerm : project.projectOwners.operations}
                onChange={(e) => {
                  setOwnerSearchField("operations");
                  setOwnerSearchTerm(e.target.value);
                  setShowOwnerDropdown(true);
                  setProject((prev) => ({
                    ...prev,
                    projectOwners: { ...prev.projectOwners, operations: e.target.value, operationsId: "" },
                  }));
                }}
                onFocus={() => {
                  setOwnerSearchField("operations");
                  setOwnerSearchTerm("");
                  setShowOwnerDropdown(true);
                }}
                placeholder="Search employee"
                disabled={isReadOnly}
              />
              {showOwnerDropdown && ownerSearchField === "operations" && ownerSearchResults.length > 0 && (
                <div className="pj-autocomplete-dropdown">
                  {sortedOptions(ownerSearchResults, (emp) => `${emp.employee_id} ${emp.name}`).map((emp) => (
                    <div
                      key={emp.employee_id}
                      className="pj-autocomplete-item"
                      onMouseDown={() => {
                        setProject((prev) => ({
                          ...prev,
                          projectOwners: { ...prev.projectOwners, operations: emp.name, operationsId: emp.employee_id },
                        }));
                        setOwnerSearchField("");
                        setOwnerSearchTerm("");
                        setShowOwnerDropdown(false);
                      }}
                    >
                      <strong>{emp.employee_id}</strong>
                      <span>{emp.name} ({emp.department || ""})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>


        {/* =================================================
            DOUBLE CREDIT
            ================================================= */}

        <div className="pj-form-grid">
          <div className="pj-form-group">
            <label>Double Credit Applicable?</label>
            <select
              value={project.doubleCreditApplicable}
              onChange={(e) => handleDoubleCreditChange(e.target.value)}
              disabled={isReadOnly}
            >
              <option value="">Select</option>
              {sortedOptions(YES_NO_OPTIONS).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {showDoubleCreditTo && (
            <div className="pj-form-group">
              <label>Double Credit To</label>
              <select
                value={project.doubleCreditTo}
                onChange={(e) => handleChange("doubleCreditTo", e.target.value)}
                disabled={isReadOnly}
              >
                <option value="">Select Department</option>
                {sortedOptions(filteredDepartments).map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>


        {/* =================================================
            PROJECT STATUS
            ================================================= */}

        <div className="pj-form-grid">
          <div className="pj-form-group">
            <label>Project Status</label>
            <select
              value={project.projectStatus}
              onChange={(e) => handleProjectStatusChange(e.target.value)}
            >
              {sortedOptions(PROJECT_STATUS_OPTIONS).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {showAbandonedReason && (
            <div className="pj-form-group">
              <label>Abandoned Reason</label>
              <textarea
                value={project.abandonedReason}
                onChange={(e) => handleChange("abandonedReason", e.target.value)}
                placeholder="Enter reason"
              />
            </div>
          )}
        </div>


      </section>

      <div className="project-actions project-form-bottom-actions">
        {(canCreateProject || canEditProject) && (
          <button type="button" className="project-form-save-draft" onClick={handleSaveDraft} disabled={formLoading}>
            {formLoading ? "Saving..." : "Save as Draft"}
          </button>
        )}
        {(canCreateProject || canEditProject) && (
          <button type="button" className="project-form-save" onClick={handleSubmit} disabled={formLoading}>
            {formLoading ? "Saving..." : "Save"}
          </button>
        )}
      </div>

      {/* =====================================================
          ADD RESOURCES
          ===================================================== */}

      {isSubmitted && (
        <section className="project-section resources-section">

          <div className="section-header">
            <div>
              <h3>Project Resources</h3>
              <p>Add resources after project submission</p>
            </div>

            <button
              type="button"
              className="pj-btn-primary"
              onClick={openAddResource}
              disabled={!canAddResources || !canEditProject}
            >
              + Add Resource
            </button>
          </div>


          {/* =================================================
              RESOURCE FORM
              ================================================= */}

          {showResourceForm && (
            <div className="resource-form">

              <div className="pj-form-grid">
                <div className="pj-form-group" ref={empDropdownRef} style={{ position: "relative" }}>
                  <label>Name *</label>
                  <input
                    type="text"
                    value={showEmpDropdown ? empSearchTerm : resourceForm.name}
                    onChange={(e) => {
                      setResourceForm((prev) => ({ ...prev, name: "", employeeId: "" }));
                      setEmpSearchTerm(e.target.value);
                      setShowEmpDropdown(true);
                    }}
                    onFocus={() => { if (!resourceForm.employeeId) { setEmpSearchTerm(resourceForm.name || ""); setShowEmpDropdown(true); } }}
                    placeholder="Search employee by name or ID..."
                  />
                  {showEmpDropdown && empSearchResults.length > 0 && (
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #D9E2EF", borderRadius: "8px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", maxHeight: "200px", overflowY: "auto", zIndex: 20, marginTop: "4px" }}>
                      {sortedOptions(empSearchResults, (emp) => `${emp.employee_id} ${emp.name || emp.first_name || ""}`).map((emp) => (
                        <div
                          key={emp.employee_id}
                          style={{ padding: "8px 12px", fontSize: "13px", cursor: "pointer", borderBottom: "1px solid #F1F5F9" }}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            const empName = emp.name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim();
                            setResourceForm((prev) => ({ ...prev, name: empName, employeeId: emp.employee_id, allocation: 100 }));
                            setEmpSearchResults([]);
                            setShowEmpDropdown(false);
                            setEmpSearchTerm("");
                            setEmployeeAllocation({ totalAllocated: 0, projectCount: 0, loading: true });
                            projectsAPI.getEmployeeAllocation(emp.employee_id)
                              .then((aRes) => {
                                const totalAllocated = Number(aRes.data?.total_allocated) || 0;
                                const projectCount = Number(aRes.data?.project_count) || 0;
                                setEmployeeAllocation({ totalAllocated, projectCount, loading: false });
                              })
                              .catch(() => setEmployeeAllocation({ totalAllocated: 0, projectCount: 0, loading: false }));
                          }}
                        >
                          <strong>{emp.employee_id}</strong> &mdash; {emp.name || `${emp.first_name || ""} ${emp.last_name || ""}`} <span style={{ color: "#94A3B8" }}>({emp.department || ""})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="pj-form-group">
                  <label>Billable</label>
                  <select
                    value={resourceForm.billable}
                    onChange={(e) => handleResourceChange("billable", e.target.value)}
                  >
                    {sortedOptions(YES_NO_OPTIONS).map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="pj-form-group">
                  <label>Project Execution At</label>
                  <select
                    value={resourceForm.projectExecutionAt}
                    onChange={(e) => handleResourceChange("projectExecutionAt", e.target.value)}
                  >
                    <option value="">Select</option>
                    {sortedOptions(PROJECT_EXECUTION_OPTIONS).map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="pj-form-group">
                  <label>Project Location</label>
                  <select
                    value={resourceForm.projectLocation}
                    onChange={(e) => handleResourceChange("projectLocation", e.target.value)}
                  >
                    <option value="">Select</option>
                    {sortedOptions(PROJECT_LOCATION_OPTIONS).map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="pj-form-group">
                  <label>Billing Start Date</label>
                  <input
                    type="date"
                    value={resourceForm.billingStartDate}
                    onChange={(e) => handleResourceChange("billingStartDate", e.target.value)}
                  />
                </div>

                <div className="pj-form-group">
                  <label>Billing End Date</label>
                  <input
                    type="date"
                    value={resourceForm.billingEndDate}
                    onChange={(e) => handleResourceChange("billingEndDate", e.target.value)}
                  />
                </div>

                {/* Resource Level */}
                {project.poSowWoModel === "Resource level" && (
                  <>
                    <div className="pj-form-group">
                      <label>PO / SOW / WO Ref</label>
                      <input
                        type="text"
                        value={resourceForm.poSowWoRef}
                        onChange={(e) => handleResourceChange("poSowWoRef", e.target.value)}
                      />
                    </div>

                    <div className="pj-form-group">
                      <label>PO Start Date</label>
                      <input
                        type="date"
                        value={resourceForm.poStartDate}
                        onChange={(e) => handleResourceChange("poStartDate", e.target.value)}
                      />
                    </div>

                    <div className="pj-form-group">
                      <label>PO End Date</label>
                      <input
                        type="date"
                        value={resourceForm.poEndDate}
                        onChange={(e) => handleResourceChange("poEndDate", e.target.value)}
                      />
                    </div>
                  </>
                )}

                {/* Role Based Models */}
                {(project.poSowWoModel === "Project level-role based rate card" ||
                  project.poSowWoModel === "Customer level- role based rate card") && (
                  <div className="pj-form-group">
                    <label>PO Role</label>
                    <select
                      value={resourceForm.poRole}
                      onChange={(e) => handleResourceChange("poRole", e.target.value)}
                    >
                      <option value="">Select Role</option>
                      {sortedOptions(rateMasterRoles.length > 0 ? rateMasterRoles : dropdownOptions.designations).map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    {rateMasterRoles.length === 0 && (
                      <span className="pj-field-hint">
                        No roles match in Rate Master for "{project.commercialModel}"
                        {project.poSowWoModel === "Customer level- role based rate card" ? ` + this customer` : ""}. Showing all designations instead. Add rates in Rate Master tab.
                      </span>
                    )}
                  </div>
                )}

                <div className="pj-form-group">
                  <label>Billing Rate Type</label>
                  <select
                    value={resourceForm.billingRateType}
                    onChange={(e) => handleResourceChange("billingRateType", e.target.value)}
                  >
                    <option value="">Select</option>
                    {sortedOptions(BILLING_RATE_TYPE_OPTIONS).map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="pj-form-group">
                  <label>Billing Rate</label>
                  {isRoleBasedPO ? (
                    <>
                      <input
                        type="number"
                        value={resourceForm.billingRate}
                        disabled
                        placeholder={rateLookup.loading ? "Looking up rate..." : rateLookup.found ? "Auto-filled from Rate Master" : "No matching rate found"}
                        style={rateLookup.searched && !rateLookup.found ? { borderColor: "#DC2626", background: "#FEF2F2" } : rateLookup.found ? { borderColor: "#059669", background: "#F0FDF4" } : {}}
                      />
                      {rateLookup.searched && !rateLookup.found && !rateLookup.loading && (
                        <span style={{ color: "#DC2626", fontSize: "12px", fontWeight: 600, marginTop: "4px", display: "block" }}>
                          No matching billing rate found for "{resourceForm.poRole}" / {resourceForm.billingRateType} / {project.currency || "INR"}. Add it in Rate Master tab.
                        </span>
                      )}
                      {rateLookup.found && rateLookup.rate && (
                        <span style={{ color: "#059669", fontSize: "12px", fontWeight: 600, marginTop: "4px", display: "block" }}>
                          Auto-filled: {rateLookup.rate.currency} {Number(rateLookup.rate.rate).toLocaleString()} / {rateLookup.rate.billing_rate_type}
                        </span>
                      )}
                    </>
                  ) : project.poSowWoModel === "Resource level" ? (
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={resourceForm.billingRate}
                      onChange={(e) => handleResourceChange("billingRate", e.target.value)}
                      placeholder="Enter billing rate"
                    />
                  ) : (
                    <input
                      type="number"
                      value={resourceForm.billingRate}
                      disabled
                      placeholder="Select PO model first"
                    />
                  )}
                </div>

                <div className="pj-form-group">
                  <label>% Allocation to Project</label>
                  {resourceForm.employeeId && !employeeAllocation.loading && employeeAllocation.projectCount > 0 && (
                    <div className="pj-allocation-warning">
                      <span className="pj-allocation-warning-title">⚠ Multiple Project Allocation</span>
                      <p>
                        This employee is already assigned to {employeeAllocation.projectCount} {employeeAllocation.projectCount === 1 ? "project" : "projects"} with a total allocation of {employeeAllocation.totalAllocated}%.
                        Additional allocation is permitted because allocation is managed independently for each project.
                        Maximum allocation for this project: 100%.
                      </p>
                    </div>
                  )}
                  {resourceForm.employeeId && (
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={resourceForm.allocation}
                      onClick={(e) => e.target.select()}
                      onChange={(e) => handleResourceChange("allocation", e.target.value)}
                    />
                  )}
                  {!resourceForm.employeeId && (
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={resourceForm.allocation}
                      disabled
                      placeholder="Select employee to set allocation"
                    />
                  )}
                </div>

                <div className="pj-form-group">
                  <label>Estimated Revenue / Month</label>
                  <input
                    type="number"
                    disabled
                    value={resourceCostPreview.estimatedRevenuePerMonth || ""}
                    placeholder={resourceForm.billingRate ? "From Rate Master per commercial model" : "Set billing rate first"}
                  />
                </div>

                <div className="pj-form-group">
                  <label>Estimated Cost / Month</label>
                  <input
                    type="number"
                    disabled
                    value={resourceCostPreview.found ? resourceCostPreview.estimatedCostPerMonth : ""}
                    placeholder={resourceForm.employeeId ? (resourceCostPreview.found ? "From Resource Cost Master" : "Not found in cost master") : "Select employee first"}
                  />
                </div>
              </div>

              <div className="resource-actions">
                <button
                  type="button"
                  className="pj-btn-secondary"
                  onClick={() => { setRateLookup({ found: false, rate: null, loading: false, searched: false }); setShowResourceForm(false); }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pj-btn-primary"
                  onClick={handleAddResource}
                >
                  Add Resource
                </button>
              </div>
            </div>
          )}


          {/* =================================================
              RESOURCE TABLE
              ================================================= */}

          {resources.length > 0 && (
            <div className="resource-table-wrapper">
              <table className="resource-table">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Billable</th>
                    <th scope="col">Execution</th>
                    <th scope="col">Location</th>
                    <th scope="col">Billing Start</th>
                    <th scope="col">Billing End</th>
                    <th scope="col">Allocation</th>
                    <th scope="col">Est. Revenue</th>
                    <th scope="col">Est. Cost</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {resources.map((resource) => (
                    <tr key={resource.id}>
                      <td>{resource.name}</td>
                      <td>{resource.billable}</td>
                      <td>{resource.projectExecutionAt}</td>
                      <td>{resource.projectLocation}</td>
                      <td>{resource.billingStartDate}</td>
                      <td>{resource.billingEndDate}</td>
                      <td>{resource.allocation}%</td>
                      <td>{resource.estimatedRevenuePerMonth != null && resource.estimatedRevenuePerMonth !== "" ? Number(resource.estimatedRevenuePerMonth).toLocaleString("en-US", { maximumFractionDigits: 2 }) : "—"}</td>
                      <td>{resource.estimatedCostPerMonth != null && resource.estimatedCostPerMonth !== "" ? Number(resource.estimatedCostPerMonth).toLocaleString("en-US", { maximumFractionDigits: 2 }) : "—"}</td>
                      <td>
                        <div className="resource-table-actions">
                          <button
                            type="button"
                            className="pj-btn-view"
                            onClick={() => handleViewResource(resource)}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            className="pj-btn-delete"
                            onClick={() => handleDeleteResource(resource.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </section>
      )}


      {/* =====================================================
          COMPLETED PROJECT
          ===================================================== */}

      {showClosureChecklist && (
        <section className="project-section">
          <div className="section-header">
            <h3>Project Closure Checklist</h3>
          </div>
          <div className="pj-form-grid" style={{ gap: "12px" }}>
            {closureChecklist.map((item, idx) => (
              <label key={item.checklistItem || idx} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
                <input
                  type="checkbox"
                  checked={!!item.isCompleted}
                  onChange={(e) => {
                    const updated = closureChecklist.map((c, i) =>
                      i === idx ? { ...c, isCompleted: e.target.checked } : c
                    );
                    setClosureChecklist(updated);
                    saveClosureChecklistDebounced(updated);
                  }}
                  disabled={!isEditing && project.projectStatus === "Completed"}
                  style={{ width: "16px", height: "16px" }}
                />
                {item.checklistItem}
                {item.isCompleted && item.completedAt && (
                  <span style={{ fontSize: "11px", color: "#888" }}>
                    (by {item.completedBy || "—"} on {new Date(item.completedAt).toLocaleDateString()})
                  </span>
                )}
              </label>
            ))}
          </div>
        </section>
      )}

      {/* =====================================================
          RESOURCE DETAIL MODAL (VIEW)
          ===================================================== */}

      {resourceToView && (
        <div className="pj-modal-overlay" onClick={() => setResourceToView(null)}>
          <div
            className="pj-modal-content pj-resource-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pj-resource-modal-header">
              <div>
                <h3>Resource Details</h3>
                <p>{resourceToView.name || resourceToView.employeeId || "Resource"}</p>
              </div>
              <button
                type="button"
                className="pj-btn-close"
                onClick={() => setResourceToView(null)}
                aria-label="Close"
              >
                &#10005;
              </button>
            </div>

            <div className="pj-detail-section">
              <h4>Resource &amp; Placement</h4>
              <div className="pj-detail-grid">
                <ResourceDetailRow label="Name" value={resourceToView.name} />
                <ResourceDetailRow label="Employee ID" value={resourceToView.employeeId} />
                <ResourceDetailRow label="Billable" value={resourceToView.billable} />
                <ResourceDetailRow label="Execution" value={resourceToView.projectExecutionAt} />
                <ResourceDetailRow label="Location" value={resourceToView.projectLocation} />
              </div>
            </div>

            <div className="pj-detail-section">
              <h4>Billing</h4>
              <div className="pj-detail-grid">
                <ResourceDetailRow label="PO / SOW / WO Ref" value={resourceToView.poSowWoRef} />
                <ResourceDetailRow label="PO Start Date" value={resourceToView.poStartDate} />
                <ResourceDetailRow label="PO End Date" value={resourceToView.poEndDate} />
                <ResourceDetailRow label="PO Role" value={resourceToView.poRole} />
                <ResourceDetailRow label="Billing Start" value={resourceToView.billingStartDate} />
                <ResourceDetailRow label="Billing End" value={resourceToView.billingEndDate} />
                <ResourceDetailRow label="Billing Rate Type" value={resourceToView.billingRateType} />
                <ResourceDetailRow
                  label="Billing Rate"
                  value={resourceToView.billingRate != null && resourceToView.billingRate !== ""
                    ? Number(resourceToView.billingRate).toLocaleString("en-US", { maximumFractionDigits: 2 })
                    : ""}
                />
                <ResourceDetailRow label="Allocation" value={
                  resourceToView.allocation != null ? `${resourceToView.allocation}%` : ""
                } />
              </div>
            </div>

            <div className="pj-detail-section">
              <h4>Calculated Values</h4>
              <div className="pj-detail-grid">
                <ResourceDetailRow
                  label="Estimated Revenue / Month"
                  value={resourceToView.estimatedRevenuePerMonth != null && resourceToView.estimatedRevenuePerMonth !== ""
                    ? Number(resourceToView.estimatedRevenuePerMonth).toLocaleString("en-US", { maximumFractionDigits: 2 })
                    : ""}
                />
                <ResourceDetailRow
                  label="Estimated Cost / Month"
                  value={resourceToView.estimatedCostPerMonth != null && resourceToView.estimatedCostPerMonth !== ""
                    ? Number(resourceToView.estimatedCostPerMonth).toLocaleString("en-US", { maximumFractionDigits: 2 })
                    : ""}
                />
              </div>
            </div>

            <div className="pj-modal-actions">
              <button
                type="button"
                className="pj-btn-secondary"
                onClick={() => setResourceToView(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProjectsTab;
